import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { toast } from 'react-toastify';
import { Mail, CheckCircle, XCircle, LogIn, Home, Download, ArrowLeft } from 'lucide-react';

// URL parametrelerinin tipi
type ConfirmationParams = {
  confirmationId: string;
};

// ---- inline styles (design-system tokens) ----
const S: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: "var(--font-family, 'Inter', -apple-system, BlinkMacSystemFont, sans-serif)",
    background: 'var(--background, #F8FAFC)',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    WebkitFontSmoothing: 'antialiased',
  },
  header: {
    height: 68,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #E2E8F0',
    background: '#fff',
    padding: '0 28px',
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  logoMark: {
    width: 38,
    height: 38,
    background: '#EEF2FF',
    borderRadius: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoInner: {
    width: 24,
    height: 24,
    background: 'linear-gradient(135deg,#6366F1 0%,#8B5CF6 100%)',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(99,102,241,.35)',
  },
  logoG: {
    color: '#fff',
    fontWeight: 900,
    fontSize: 13,
    letterSpacing: '-0.03em',
  },
  logoTxt: {
    fontWeight: 800,
    fontSize: 17,
    color: '#0F172A',
    letterSpacing: '-0.025em',
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
  },
  card: {
    background: '#fff',
    borderRadius: 32,
    padding: '56px 48px',
    width: '100%',
    maxWidth: 460,
    boxShadow: '0 4px 16px -4px rgba(15,23,42,.08), 0 1px 2px rgba(15,23,42,.04)',
    textAlign: 'center',
    animation: 'fadeUp .65s cubic-bezier(.22,1,.36,1) both',
  },
  // loading
  loaderRingWrap: {
    width: 80,
    height: 80,
    margin: '0 auto 32px',
    position: 'relative',
  },
  loaderIcon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6366F1',
  },
  progressTrack: {
    height: 3,
    background: '#F1F5F9',
    borderRadius: 9999,
    margin: '28px 0',
    overflow: 'hidden',
  },
  // icon circle
  iconCircle: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    margin: '0 auto 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconSuccess: { background: '#D1FAE5' },
  iconError: { background: '#FEF2F2' },
  stateTitle: {
    fontSize: 26,
    fontWeight: 900,
    letterSpacing: '-0.035em',
    color: '#0F172A',
    marginBottom: 12,
  },
  stateDesc: {
    fontSize: 15,
    color: '#64748B',
    lineHeight: 1.65,
    marginBottom: 36,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 56,
    border: 'none',
    borderRadius: 24,
    background: 'linear-gradient(135deg,#6366F1 0%,#8B5CF6 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 16,
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(99,102,241,.3)',
    textDecoration: 'none',
    marginBottom: 12,
    transition: 'opacity .15s, transform .12s',
    fontFamily: 'inherit',
  },
  btnGhost: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 50,
    border: '1.5px solid #E2E8F0',
    borderRadius: 16,
    background: '#fff',
    color: '#334155',
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'border-color .15s, background .15s',
    fontFamily: 'inherit',
  },
  footer: {
    padding: '24px',
    textAlign: 'center',
    fontSize: 12,
    color: '#94A3B8',
  },
  footerLink: {
    color: '#64748B',
    textDecoration: 'none',
    fontWeight: 500,
  },
  countdownWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 32,
  },
  countdownTxt: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: 500,
  },
  noShowTxt: {
    fontSize: 13,
    color: '#94A3B8',
  },
};

// Spinner ring SVG
const SpinnerRing = () => (
  <svg
    width="80"
    height="80"
    viewBox="0 0 80 80"
    style={{ position: 'absolute', inset: 0, animation: 'spin .9s linear infinite' }}
  >
    <circle cx="40" cy="40" r="36" fill="none" stroke="#EEF2FF" strokeWidth="4" />
    <circle
      cx="40" cy="40" r="36"
      fill="none"
      stroke="url(#spinGrad)"
      strokeWidth="4"
      strokeDasharray="56 170"
      strokeLinecap="round"
    />
    <defs>
      <linearGradient id="spinGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#6366F1" />
        <stop offset="100%" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
  </svg>
);

/**
 * Hesap doğrulama sayfası
 */
const ConfirmationPage = () => {
  const { t } = useTranslation();
  const { confirmationId } = useParams<ConfirmationParams>();
  const navigate = useNavigate();
  const { apiProxyService } = useApi();

  // State değişkenleri
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);

  // Sayfa yüklendiğinde doğrulama işlemini başlat
  useEffect(() => {
    const confirmAccount = async () => {
      if (!confirmationId) {
        setError('errors.confirmation.missingId');
        setIsLoading(false);
        setTimeout(() => { navigate('/'); }, 3000);
        return;
      }

      try {
        const response = await apiProxyService.confirmAccount(confirmationId);
        if (response.success) {
          setIsConfirmed(true);
          toast.success(t('common.notifications.accountConfirmed'));
        } else {
          setError(response.error || t('errors.confirmation.defaultError'));
        }
      } catch (err) {
        console.error('Hesap doğrulama hatası:', err);
        setError(t('errors.confirmation.defaultError'));
      } finally {
        setIsLoading(false);
        setTimeout(() => { navigate('/'); }, 3000);
      }
    };

    confirmAccount();
  }, [confirmationId, apiProxyService, navigate, t]);

  // Countdown for success state
  useEffect(() => {
    if (!isConfirmed) return;
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [isConfirmed, countdown]);

  const handleGoToHome = () => navigate('/');

  // Countdown ring math
  const fullDash = 113.1;
  const offset = fullDash * (1 - countdown / 5);

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(.5); } to { opacity: 1; transform: scale(1); } }
        .conf-card { animation: fadeUp .65s cubic-bezier(.22,1,.36,1) both; }
        .conf-icon-circle { animation: scaleIn .5s cubic-bezier(.34,1.56,.64,1) both; }
        .conf-btn-primary:hover { opacity: .92; transform: translateY(-1px); }
        .conf-btn-ghost:hover { border-color: #CBD5E1 !important; background: #F8FAFC !important; }
      `}</style>

      <div style={S.page}>
        {/* Header */}
        <header style={S.header}>
          <div style={S.logoLink} onClick={handleGoToHome}>
            <div style={S.logoMark}>
              <div style={S.logoInner}>
                <span style={S.logoG}>G</span>
              </div>
            </div>
            <span style={S.logoTxt}>GetSpeakHub</span>
          </div>
        </header>

        {/* Main */}
        <main style={S.main}>
          <div style={S.card} className="conf-card">

            {/* LOADING STATE */}
            {isLoading && (
              <div>
                <div style={S.loaderRingWrap}>
                  <div style={{ background: '#EEF2FF', borderRadius: '50%', width: 80, height: 80, position: 'absolute' }} />
                  <SpinnerRing />
                  <div style={S.loaderIcon}>
                    <Mail size={28} />
                  </div>
                </div>
                <h2 style={S.stateTitle}>{t('common.confirmation.loading') || 'Verifying your account…'}</h2>
                <p style={S.stateDesc}>Hang tight — we're confirming your email address. This takes just a moment.</p>
                <div style={S.progressTrack}>
                  <div style={{
                    height: '100%',
                    borderRadius: 9999,
                    background: 'linear-gradient(135deg,#6366F1 0%,#8B5CF6 100%)',
                    width: '60%',
                    transition: 'width .5s ease',
                  }} />
                </div>
                <p style={S.noShowTxt}>Do not close this tab.</p>
              </div>
            )}

            {/* SUCCESS STATE */}
            {!isLoading && isConfirmed && (
              <div>
                <div style={{ ...S.iconCircle, ...S.iconSuccess }} className="conf-icon-circle">
                  <CheckCircle size={44} color="#10B981" strokeWidth={2} />
                </div>
                <h2 style={S.stateTitle}>{t('common.confirmation.success') || "You're all set."}</h2>
                <p style={S.stateDesc}>
                  Your account has been confirmed. Welcome to GetSpeakHub — your first conversation is waiting.
                </p>

                {countdown > 0 && (
                  <div style={S.countdownWrap}>
                    <div style={{ width: 44, height: 44, position: 'relative', flexShrink: 0 }}>
                      <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx="22" cy="22" r="18" fill="none" stroke="#E0E7FF" strokeWidth="3" />
                        <circle
                          cx="22" cy="22" r="18"
                          fill="none"
                          stroke="#6366F1"
                          strokeWidth="3"
                          strokeDasharray={fullDash}
                          strokeDashoffset={offset}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset .9s linear' }}
                        />
                      </svg>
                      <div style={{
                        position: 'absolute', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 15, fontWeight: 800, color: '#6366F1',
                      }}>
                        {countdown}
                      </div>
                    </div>
                    <span style={S.countdownTxt}>Redirecting to the app…</span>
                  </div>
                )}

                <button
                  style={S.btnPrimary}
                  className="conf-btn-primary"
                  onClick={() => navigate('/login')}
                >
                  <LogIn size={18} />
                  {t('common.confirmation.goToHome') || 'Sign In Now'}
                </button>
                <button
                  style={S.btnGhost}
                  className="conf-btn-ghost"
                  onClick={handleGoToHome}
                >
                  <Home size={16} />
                  Back to Home
                </button>
              </div>
            )}

            {/* ERROR STATE */}
            {!isLoading && !isConfirmed && (
              <div>
                <div style={{ ...S.iconCircle, ...S.iconError }} className="conf-icon-circle">
                  <XCircle size={44} color="#EF4444" strokeWidth={2} />
                </div>
                <h2 style={S.stateTitle}>{t('common.confirmation.failed') || 'Confirmation failed.'}</h2>
                <p style={S.stateDesc}>
                  {error && t(error)}
                  {!error && 'This confirmation link is invalid or has already been used. Please request a new one from the app.'}
                </p>
                <p style={{ ...S.stateDesc, marginBottom: 32 }}>{t('common.confirmation.redirecting')}</p>

                <button
                  style={S.btnPrimary}
                  className="conf-btn-primary"
                  onClick={() => window.open('https://getspeakhub.com/download', '_blank')}
                >
                  <Download size={18} />
                  Get the App
                </button>
                <button
                  style={S.btnGhost}
                  className="conf-btn-ghost"
                  onClick={handleGoToHome}
                >
                  <ArrowLeft size={16} />
                  Back to Home
                </button>
              </div>
            )}

          </div>
        </main>

        {/* Footer */}
        <footer style={S.footer}>
          <a href="/terms" style={S.footerLink}>Terms of Service</a>
          {' · '}
          <a href="/privacy" style={S.footerLink}>Privacy Policy</a>
          {' · '}
          © 2026 GetSpeakHub, Inc.
        </footer>
      </div>
    </>
  );
};

export default ConfirmationPage;
